import { getFunctionUrl } from "./functionEndpoints.js";
import {
  createHttpError,
  withRefreshedAccessTokenRetry,
} from "./googleAuthSession.js";

const ACTIVE_HEADER = "active";
const FLAGS_HEADER = "flags";

export const FLAG_STATUS_NONE = "none";
export const FLAG_STATUS_FLAGGED = "flagged";
export const FLAG_STATUS_REVERSE_REQUIRED = "reverse_required";

const normalizeHeaderKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeCellText = (value) => String(value ?? "").trim().toLowerCase();

const columnIndexToLetters = (columnIndex) => {
  let index = Number(columnIndex);
  if (!Number.isFinite(index) || index < 0) {
    throw new Error(`Invalid column index: ${columnIndex}`);
  }

  let letters = "";
  while (index >= 0) {
    letters = String.fromCharCode((index % 26) + 65) + letters;
    index = Math.floor(index / 26) - 1;
  }
  return letters;
};

const postFunctionJson = async (functionName, payload) => {
  return withRefreshedAccessTokenRetry(payload, async (nextPayload) => {
    const response = await fetch(getFunctionUrl(functionName), {
      body: JSON.stringify(nextPayload),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw createHttpError(
        body?.error || `Failed ${functionName}: ${response.status}`,
        response.status,
        body
      );
    }
    return body;
  });
};

const fetchSheetValues = async (spreadsheetAccessData, range) => {
  return withRefreshedAccessTokenRetry(
    spreadsheetAccessData,
    async (nextAccessData) => {
      const response = await fetch(
        getFunctionUrl("get-sheet-values"),
        {
          body: JSON.stringify({
            range,
            ...nextAccessData,
          }),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        }
      );
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw createHttpError(
          body?.error || `Failed get-sheet-values: ${response.status}`,
          response.status,
          body
        );
      }
      return body;
    }
  );
};

export const getCardsSheetInfo = (metadata = []) => {
  const matchingSheet =
    metadata.find(
      (sheet) => String(sheet?.properties?.title ?? "").trim() === "Sheet1"
    ) ??
    metadata.find(
      (sheet) =>
        String(sheet?.properties?.title ?? "").trim().toLowerCase() === "cards"
    ) ??
    metadata[0];

  return {
    sheetId: matchingSheet?.properties?.sheetId,
    sheetTitle: matchingSheet?.properties?.title ?? "",
  };
};

export const parseCardFlagState = (value) => {
  const tokens = normalizeCellText(value)
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

  const muted = tokens.includes("muted");
  const flagStatus = tokens.includes("reverse required")
    ? FLAG_STATUS_REVERSE_REQUIRED
    : tokens.includes("flagged")
      ? FLAG_STATUS_FLAGGED
      : FLAG_STATUS_NONE;

  return {
    muted,
    flagStatus,
    flagged: flagStatus === FLAG_STATUS_FLAGGED,
    reverseRequired: flagStatus === FLAG_STATUS_REVERSE_REQUIRED,
  };
};

export const serializeCardFlagState = ({ muted = false, flagStatus } = {}) => {
  const values = [];
  if (muted) {
    values.push("muted");
  }
  if (flagStatus === FLAG_STATUS_REVERSE_REQUIRED) {
    values.push("reverse required");
  } else if (flagStatus === FLAG_STATUS_FLAGGED) {
    values.push("flagged");
  }
  return values.join(", ");
};

export const getHistoryDerivedActive = (history = {}, cardId) =>
  Array.isArray(history?.[cardId]) && history[cardId].length > 0;

export const applyHistoryActiveStateToCards = (cards = [], history = {}) =>
  cards.map((card) => ({
    ...card,
    active: getHistoryDerivedActive(history, card.card_id),
  }));

export const buildActiveColumnValues = (cards = []) =>
  cards.map((card) => [card.active ? "x" : ""]);

export const applyPersistedCardSheetState = (
  card = {},
  { includeActive = true } = {}
) => ({
  ...card,
  persistedActive: includeActive ? !!card.active : !!card.persistedActive,
  persistedMuted: !!card.muted,
  persistedFlagStatus: card.flagStatus ?? FLAG_STATUS_NONE,
});

export const hasCardSheetStateChanged = (
  previousCard = {},
  nextCard = {},
  { includeActive = true } = {}
) => {
  const previousActive =
    previousCard.persistedActive !== undefined
      ? !!previousCard.persistedActive
      : !!previousCard.active;
  const previousMuted =
    previousCard.persistedMuted !== undefined
      ? !!previousCard.persistedMuted
      : !!previousCard.muted;
  const previousFlagStatus =
    previousCard.persistedFlagStatus ?? previousCard.flagStatus ?? FLAG_STATUS_NONE;

  if (includeActive && previousActive !== !!nextCard.active) {
    return true;
  }

  return (
    previousMuted !== !!nextCard.muted ||
    previousFlagStatus !== (nextCard.flagStatus ?? FLAG_STATUS_NONE)
  );
};

export const buildCardColumnIndexes = (headers = []) => {
  const normalizedHeaders = headers.map(normalizeHeaderKey);
  const activeIndex = normalizedHeaders.indexOf(ACTIVE_HEADER);
  const flagsIndex = normalizedHeaders.indexOf(FLAGS_HEADER);
  const baseOffset = activeIndex === 0 && flagsIndex === 1 ? 2 : 0;
  const findIndex = (candidates, fallback) => {
    const foundIndex = normalizedHeaders.findIndex((header) =>
      candidates.includes(header)
    );
    return foundIndex >= 0 ? foundIndex : fallback;
  };

  return {
    activeIndex,
    flagsIndex,
    frontIndex: findIndex(["front"], baseOffset),
    backIndex: findIndex(["back"], baseOffset + 1),
    frontAdditionalIndex: findIndex(
      ["frontadditional", "frontnotes", "frontnote"],
      baseOffset + 2
    ),
    backAdditionalIndex: findIndex(
      ["backadditional", "backnotes", "backnote"],
      baseOffset + 3
    ),
  };
};

const insertCardStateColumns = async (
  spreadsheetAccessData,
  sheetId
) => {
  return postFunctionJson("batch-update-sheet", {
    ...spreadsheetAccessData,
    requests: [
      {
        insertDimension: {
          range: {
            sheetId,
            dimension: "COLUMNS",
            startIndex: 0,
            endIndex: 2,
          },
          inheritFromBefore: false,
        },
      },
    ],
  });
};

const writeCardHeaders = async (spreadsheetAccessData, sheetTitle) => {
  const range = `${sheetTitle}!A1:B1`;
  return postFunctionJson("update-sheet-values", {
    ...spreadsheetAccessData,
    range,
    values: [[ACTIVE_HEADER, FLAGS_HEADER]],
    valueInputOption: "USER_ENTERED",
  });
};

export const ensureCardsSheetColumns = async (
  metadata,
  spreadsheetAccessData
) => {
  const { sheetId, sheetTitle } = getCardsSheetInfo(metadata);
  const initialSheet = await fetchSheetValues(
    spreadsheetAccessData,
    `${sheetTitle}!A:Z`
  );
  const initialValues = Array.isArray(initialSheet?.values)
    ? initialSheet.values
    : [];
  const initialHeaders = initialValues[0] ?? [];
  const indexes = buildCardColumnIndexes(initialHeaders);

  if (indexes.activeIndex >= 0 && indexes.flagsIndex >= 0) {
    return {
      sheetTitle,
      values: initialValues,
    };
  }

  await insertCardStateColumns(spreadsheetAccessData, sheetId);
  await writeCardHeaders(spreadsheetAccessData, sheetTitle);

  const refreshedSheet = await fetchSheetValues(
    spreadsheetAccessData,
    `${sheetTitle}!A:Z`
  );

  return {
    sheetTitle,
    values: Array.isArray(refreshedSheet?.values) ? refreshedSheet.values : [],
  };
};

export const updateCardSheetState = async (
  spreadsheetAccessData,
  card,
  { includeActive = true } = {}
) => {
  if (!card?.cardsSheetTitle || !card?.sheetRowNumber) {
    return null;
  }

  const range = includeActive
    ? `${card.cardsSheetTitle}!A${card.sheetRowNumber}:B${card.sheetRowNumber}`
    : `${card.cardsSheetTitle}!B${card.sheetRowNumber}:B${card.sheetRowNumber}`;
  const values = includeActive
    ? [
        [
          card.active ? "x" : "",
          serializeCardFlagState({
            muted: !!card.muted,
            flagStatus: card.flagStatus ?? FLAG_STATUS_NONE,
          }),
        ],
      ]
    : [
        [
          serializeCardFlagState({
            muted: !!card.muted,
            flagStatus: card.flagStatus ?? FLAG_STATUS_NONE,
          }),
        ],
      ];
  return postFunctionJson("update-sheet-values", {
    ...spreadsheetAccessData,
    range,
    values,
    valueInputOption: "USER_ENTERED",
  });
};

export const syncActiveColumnWithHistory = async (
  spreadsheetAccessData,
  cards = [],
  history = {}
) => {
  if (!Array.isArray(cards) || cards.length === 0) {
    return cards;
  }

  const nextCards = applyHistoryActiveStateToCards(cards, history);
  const hasMismatch = nextCards.some(
    (card, index) => !!card.active !== !!cards[index]?.active
  );

  if (!hasMismatch) {
    return nextCards;
  }

  const sheetTitle = nextCards[0]?.cardsSheetTitle;
  if (!sheetTitle) {
    return nextCards;
  }

  const startRow = nextCards[0]?.sheetRowNumber ?? 2;
  const endRow = nextCards[nextCards.length - 1]?.sheetRowNumber ?? startRow;
  const range = `${sheetTitle}!A${startRow}:A${endRow}`;
  await postFunctionJson("update-sheet-values", {
    ...spreadsheetAccessData,
    range,
    values: buildActiveColumnValues(nextCards),
    valueInputOption: "USER_ENTERED",
  });
  return nextCards.map((card) => applyPersistedCardSheetState(card));
};

export const updateCardContent = async (spreadsheetAccessData, card) => {
  if (!card?.cardsSheetTitle || !card?.sheetRowNumber || !card?.cardsSheetColumns) {
    return null;
  }

  const {
    frontIndex,
    backIndex,
    frontAdditionalIndex,
    backAdditionalIndex,
  } = card.cardsSheetColumns;
  const indexes = [
    frontIndex,
    backIndex,
    frontAdditionalIndex,
    backAdditionalIndex,
  ];

  if (indexes.some((index) => !Number.isFinite(index) || index < 0)) {
    throw new Error("Missing card content column indexes");
  }

  const sortedIndexes = [...indexes].sort((left, right) => left - right);
  const startColumnIndex = sortedIndexes[0];
  const endColumnIndex = sortedIndexes[sortedIndexes.length - 1];
  const values = new Array(endColumnIndex - startColumnIndex + 1).fill("");

  values[frontIndex - startColumnIndex] = card.front ?? "";
  values[backIndex - startColumnIndex] = card.back ?? "";
  values[frontAdditionalIndex - startColumnIndex] = card.frontAdditional ?? "";
  values[backAdditionalIndex - startColumnIndex] = card.backAdditional ?? "";

  const range = `${card.cardsSheetTitle}!${columnIndexToLetters(startColumnIndex)}${card.sheetRowNumber}:${columnIndexToLetters(endColumnIndex)}${card.sheetRowNumber}`;

  return postFunctionJson("update-sheet-values", {
    ...spreadsheetAccessData,
    range,
    values: [values],
    valueInputOption: "USER_ENTERED",
  });
};
